import { ReactElement, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  InputEmail,
  AdaButton,
  AdaLink,
  Icon,
  InputContainer,
  InputText,
  useBoolean,
} from '@betrybe/ada-react';

import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { DefaultLayout } from '@/layouts';

import { loginValidationRules } from './schema';
import AuthenticationService from '@/services/authentication/authentication';

import { useToken } from '@/hooks';
import { useToastNotification } from '@/contexts';

function Login(): ReactElement {
  const [, toastNotificationDispatch] = useToastNotification();
  const token = useToken();
  const history = useHistory();
  const [passwordIsHidden, { toggle: togglePasswordVisibility }] =
    useBoolean(true);
  const { login } = AuthenticationService();
  const [isLoading, setLoading] = useState<boolean>(false);
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object().shape({
      email: loginValidationRules.requiredEmail,
      password: loginValidationRules.requiredPassword,
    }),
    onSubmit: async (values) => {
      setLoading(true);
      login(values)
        .then((res) => {
          const { data } = res;
          token.setToken(data.data.access_token);
          token.setLoginIndentityData(data);
          const {
            data: { user },
          } = data;

          if (user?.roles?.includes('student')) {
            history.push('/payment-history');
          } else {
            history.push('/callback');
          }
        })
        .catch((err) => {
          toastNotificationDispatch({
            type: 'close',
            payload: { text: '', title: '' },
          });

          toastNotificationDispatch({
            type: 'error',
            payload: {
              title: 'Autenticação inválida',
              text: 'Login e/ou senha incorretos',
            },
          });
          Sentry.captureException(err);
        })
        .finally(() => setLoading(false));
    },
  });

  return (
    <DefaultLayout
      title="Entre para continuar"
      description="Utilize o e-mail e senha cadastrados na Trybe"
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="login-container">
          <div className="login-container__email-section">
            <InputContainer
              label="Qual é o seu e-mail?"
              error={formik.touched.email && formik.errors.email}
            >
              {(inputProps) => (
                <InputEmail
                  {...inputProps}
                  id="input-login-email"
                  name="email"
                  placeholder="ex: ada@lovelace.com"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  autoComplete="username"
                />
              )}
            </InputContainer>
          </div>

          <div className="login-container__password-section">
            <InputContainer
              label="Informe sua senha, por favor"
              error={formik.touched.password && formik.errors.password}
            >
              {(inputProps) => (
                <div className="login-container__hide-password">
                  <div className="login-container__hide-password--input">
                    <InputText
                      {...inputProps}
                      id="input-login-password"
                      type={passwordIsHidden ? 'password' : 'text'}
                      name="password"
                      placeholder="•••••••••••"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                      autoComplete="current-password"
                    />
                  </div>
                  <div className="login-container__hide-password--button">
                    <button type="button" onClick={togglePasswordVisibility}>
                      <Icon
                        name={
                          passwordIsHidden ? 'visibility_off' : 'visibility'
                        }
                      />
                    </button>
                  </div>
                </div>
              )}
            </InputContainer>

            <div className="login-container__password-section--forgot-password">
              <AdaLink href="/forgot-password">Esqueci minha senha</AdaLink>
            </div>
          </div>

          <div className="login-container__submit-button">
            <AdaButton
              id="button-login-submit"
              loading={isLoading}
              type="submit"
            >
              Entrar
            </AdaButton>
          </div>
        </div>
      </form>
    </DefaultLayout>
  );
}

export default Login;
